import { PulseEnvironment } from '@pulse/browser-shared';
import { BehaviorSubject, Observable, Subject, race, timer } from 'rxjs';
import { debounceTime, map, take, tap } from 'rxjs/operators';

const DOM_IDLE_MS = 500;
const MAX_WAIT_MS = 5000;

export class PageLoadedService {
    private readonly pageLoaded = new BehaviorSubject<boolean>(false);

    readonly pageLoaded$ = this.pageLoaded.asObservable();

    constructor(private readonly env: PulseEnvironment) {}

    whenLoaded(): Observable<boolean> {
        const win = this.env.win;

        if (!win?.document) {
            this.pageLoaded.next(true);

            return timer(0).pipe(
                map(() => true),
                take(1),
            );
        }

        const domIdle$ = new Subject<void>();

        try {
            const body = win.document.body ?? win.document.documentElement;

            if (body) {
                const observer = new MutationObserver(() => domIdle$.next());
                observer.observe(body, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    characterData: true,
                });
            }

            domIdle$.next();
        } catch {
            domIdle$.next();
        }

        const domIdleDone$ = domIdle$.pipe(
            debounceTime(DOM_IDLE_MS),
            take(1),
            map(() => true),
        );
        const timeout$ = timer(MAX_WAIT_MS).pipe(map(() => true));

        return race(domIdleDone$, timeout$).pipe(
            take(1),
            tap(() => this.pageLoaded.next(true)),
        );
    }
}
